import React, { useState } from 'react';
import { useFormik } from 'formik';
import { useMutation } from 'react-query';
import * as Yup from 'yup';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Checkbox,
  FormControlLabel,
  AppBar,
  Toolbar,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  TrendingUp,
  LocationOn,
  Assessment,
  Business,
  BarChart,
  Search,
  Verified,
  Security,
  Speed,
  AccountBalance,
  Apartment,
  Handshake,
  Menu as MenuIcon,
} from '@mui/icons-material';
import {
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { LoaderButton } from '../components/common/LoaderButton';
import { useToast } from '../components/common/ToastProvider';
import { contactAPI } from '../services/api';

const PHONE_DIGITS_COUNT = 10;

// Маска номера США: (XXX) XXX-XXXX. Проверяем только количество цифр,
// т.к. маска может добавлять служебные символы типа _() -
const getPhoneDigits = (value?: string): string =>
  (value ?? '').replace(/\D/g, '');

const formatPhoneNumber = (value: string): string => {
  const digits = getPhoneDigits(value).slice(0, PHONE_DIGITS_COUNT);
  const area = digits.slice(0, 3);
  const prefix = digits.slice(3, 6);
  const line = digits.slice(6, PHONE_DIGITS_COUNT);

  let result = '';
  if (area) result += `(${area}`;
  if (area.length === 3) result += ') ';
  result += prefix;
  if (prefix.length === 3 && line) result += '-';
  result += line;
  return result;
};

const ContactSchema = Yup.object().shape({
  firstName: Yup.string().required('Required'),
  lastName: Yup.string().required('Required'),
  email: Yup.string().email('Invalid email').required('Required'),
  // необязательное поле, но если цифры введены - номер должен быть полным
  phoneNumber: Yup.string().test(
    'phone-complete',
    'Invalid phone number',
    (value) => {
      const digits = getPhoneDigits(value);
      return digits.length === 0 || digits.length === PHONE_DIGITS_COUNT;
    }
  ),
  message: Yup.string(),
});

const navLinks = [
  { label: 'Services', href: '#services' },
  { label: 'Why Us', href: '#why-us' },
  { label: 'Contact', href: '#contact' },
];

export const LandingPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const sendMessageMutation = useMutation(contactAPI.sendMessage, {
    onSuccess: () => {
      showToast('Your message has been sent successfully!', 'success');
      formik.resetForm();
    },
    onError: () => {
      showToast('Failed to send message. Please try again later.', 'error');
    },
  });

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      message: '',
      contactConsent: false,
    },
    validationSchema: ContactSchema,
    onSubmit: (values) => {
      sendMessageMutation.mutate({
        ...values,
        // отправляем phoneNumber без кода страны, только цифры
        phoneNumber: getPhoneDigits(values.phoneNumber) || undefined,
      });
    },
  });

  const scrollTo = (href: string) => {
    const el = document.querySelector(href);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileNavOpen(false);
  };

  const services = [
    {
      icon: (
        <Assessment
          fontSize='large'
          color='primary'
        />
      ),
      title: 'Market Feasibility Studies',
      text: 'Understand demand, absorption rates, demographic trends, and competitive positioning before committing capital to a project.',
    },
    {
      icon: (
        <BarChart
          fontSize='large'
          color='primary'
        />
      ),
      title: 'CRE Market Analysis',
      text: 'Comprehensive insights into office, retail, industrial, multifamily, mixed-use, and hospitality sectors across regional and national markets.',
    },
    {
      icon: (
        <TrendingUp
          fontSize='large'
          color='primary'
        />
      ),
      title: 'Investment & Risk Analysis',
      text: 'Evaluate opportunities with detailed financial modeling, market forecasting, and risk assessments designed to support strategic investment decisions.',
    },
    {
      icon: (
        <LocationOn
          fontSize='large'
          color='primary'
        />
      ),
      title: 'Site Selection & Location Intelligence',
      text: 'Identify high-potential markets and optimal locations using demographic, economic, and geographic data analysis.',
    },
    {
      icon: (
        <Search
          fontSize='large'
          color='primary'
        />
      ),
      title: 'Competitive Benchmarking',
      text: 'Track competitors, rental trends, occupancy performance, pricing strategies, and market positioning to maintain a competitive edge.',
    },
    {
      icon: (
        <Business
          fontSize='large'
          color='primary'
        />
      ),
      title: 'Custom Research Solutions',
      text: 'Every project is unique. We develop customized research and reporting solutions aligned with your investment goals and operational needs.',
    },
  ];

  const whyUs = [
    {
      icon: (
        <Apartment
          fontSize='large'
          color='primary'
        />
      ),
      title: 'Industry-Focused Expertise',
      text: 'Our team specializes exclusively in commercial real estate research and analytics, giving clients actionable insights grounded in industry knowledge.',
    },
    {
      icon: (
        <Speed
          fontSize='large'
          color='primary'
        />
      ),
      title: 'Data-Driven Decision Making',
      text: 'We combine market intelligence, economic indicators, demographic analysis, and proprietary research methodologies to deliver accurate, meaningful insights.',
    },
    {
      icon: (
        <Verified
          fontSize='large'
          color='primary'
        />
      ),
      title: 'Strategic Insight',
      text: 'Beyond reporting data, we interpret market trends and translate findings into practical recommendations that support growth and profitability.',
    },
    {
      icon: (
        <Security
          fontSize='large'
          color='primary'
        />
      ),
      title: 'Reliable & Confidential',
      text: 'We understand the sensitivity of investment decisions and maintain the highest standards of professionalism, accuracy, and confidentiality.',
    },
  ];

  const whoWeServe = [
    'Commercial Real Estate Investors',
    'Developers & Builders',
    'Asset Managers',
    'Property Owners',
    'Brokerage Firms',
    'Private Equity & Investment Groups',
    'Financial Institutions',
    'Urban Planning & Development Firms',
  ];

  return (
    <Box sx={{ overflowX: 'hidden' }}>
      {/* Navbar */}
      <AppBar
        position='sticky'
        color='default'
        elevation={1}
        sx={{ bgcolor: '#fff' }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Typography
            variant='h6'
            sx={{ fontWeight: 700, color: '#0d1b2a', cursor: 'pointer' }}
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            SR Real Estate Services
          </Typography>

          {isMobile ? (
            <>
              <IconButton onClick={() => setMobileNavOpen(true)}>
                <MenuIcon />
              </IconButton>
              <Drawer
                anchor='right'
                open={mobileNavOpen}
                onClose={() => setMobileNavOpen(false)}
              >
                <Box sx={{ width: 240, p: 2 }}>
                  <List>
                    {navLinks.map((link) => (
                      <ListItem
                        key={link.label}
                        onClick={() => scrollTo(link.href)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <ListItemText primary={link.label} />
                      </ListItem>
                    ))}
                  </List>
                  <LoaderButton
                    variant='contained'
                    fullWidth
                    onClick={() => navigate('/admin/login')}
                  >
                    Dashboard
                  </LoaderButton>
                </Box>
              </Drawer>
            </>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              {navLinks.map((link) => (
                <Typography
                  key={link.label}
                  onClick={() => scrollTo(link.href)}
                  sx={{
                    cursor: 'pointer',
                    color: 'text.primary',
                    fontWeight: 500,
                    '&:hover': { color: 'primary.main' },
                  }}
                >
                  {link.label}
                </Typography>
              ))}
              <LoaderButton
                variant='contained'
                onClick={() => navigate('/admin/login')}
              >
                Dashboard
              </LoaderButton>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Hero */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0d1b2a 0%, #1b263b 100%)',
          color: '#fff',
          py: { xs: 8, md: 14 },
          px: 2,
          textAlign: 'center',
        }}
      >
        <Container maxWidth='md'>
          <Typography
            variant='overline'
            sx={{ color: '#90e0ef', letterSpacing: 2, mb: 2, display: 'block' }}
          >
            Commercial Real Estate Intelligence
          </Typography>
          <Typography
            variant='h2'
            sx={{
              fontWeight: 800,
              mb: 3,
              fontSize: { xs: '2rem', md: '3.5rem' },
            }}
          >
            Intelligence That Drives Smarter Decisions
          </Typography>
          <Typography
            variant='h5'
            sx={{
              color: '#caf0f8',
              mb: 4,
              fontWeight: 400,
              fontSize: { xs: '1.1rem', md: '1.5rem' },
            }}
          >
            Market Research & Analysis for Investors, Developers, and CRE
            Professionals
          </Typography>
          <Typography
            variant='body1'
            sx={{
              color: '#e0e1dd',
              maxWidth: 800,
              mx: 'auto',
              mb: 5,
              lineHeight: 1.7,
            }}
          >
            At SR Real Estate Services (formerly Solutions & Researches Pro),
            we provide data-driven market
            research and strategic analysis tailored specifically for the
            commercial real estate industry. From emerging market opportunities
            to investment risk assessments, we help clients make confident,
            informed decisions backed by reliable intelligence.
          </Typography>
          <LoaderButton
            variant='contained'
            size='large'
            sx={{
              bgcolor: '#00b4d8',
              '&:hover': { bgcolor: '#0077b6' },
              px: 4,
              py: 1.5,
            }}
            onClick={() => scrollTo('#contact')}
          >
            Contact Us
          </LoaderButton>
        </Container>
      </Box>

      {/* Services */}
      <Box
        id='services'
        sx={{ py: { xs: 6, md: 10 }, bgcolor: '#f8f9fa' }}
      >
        <Container maxWidth='lg'>
          <Typography
            variant='h3'
            align='center'
            sx={{
              fontWeight: 700,
              mb: 1,
              color: '#0d1b2a',
              fontSize: { xs: '1.8rem', md: '2.5rem' },
            }}
          >
            Our Services
          </Typography>
          <Typography
            variant='subtitle1'
            align='center'
            sx={{ color: 'text.secondary', mb: 6, maxWidth: 700, mx: 'auto' }}
          >
            Comprehensive research services designed to support every stage of
            your commercial real estate journey.
          </Typography>
          <Grid
            container
            spacing={4}
          >
            {services.map((s) => (
              <Grid
                size={{ xs: 12, md: 6, lg: 4 }}
                key={s.title}
              >
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: 3,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                    transition: 'transform 0.2s',
                    '&:hover': { transform: 'translateY(-4px)' },
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ mb: 2 }}>{s.icon}</Box>
                    <Typography
                      variant='h6'
                      sx={{ fontWeight: 700, mb: 1, color: '#0d1b2a' }}
                    >
                      {s.title}
                    </Typography>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                    >
                      {s.text}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Why Choose Us */}
      <Box
        id='why-us'
        sx={{ py: { xs: 6, md: 10 } }}
      >
        <Container maxWidth='lg'>
          <Typography
            variant='h3'
            align='center'
            sx={{
              fontWeight: 700,
              mb: 1,
              color: '#0d1b2a',
              fontSize: { xs: '1.8rem', md: '2.5rem' },
            }}
          >
            Why Choose SR Real Estate Services
          </Typography>
          <Typography
            variant='subtitle1'
            align='center'
            sx={{ color: 'text.secondary', mb: 6 }}
          >
            We combine deep industry knowledge with rigorous analytical methods.
          </Typography>
          <Grid
            container
            spacing={4}
          >
            {whyUs.map((item) => (
              <Grid
                size={{ xs: 12, md: 6 }}
                key={item.title}
              >
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: 3,
                    boxShadow: 'none',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <CardContent sx={{ p: 4, display: 'flex', gap: 3 }}>
                    <Box sx={{ mt: 0.5 }}>{item.icon}</Box>
                    <Box>
                      <Typography
                        variant='h6'
                        sx={{ fontWeight: 700, mb: 1, color: '#0d1b2a' }}
                      >
                        {item.title}
                      </Typography>
                      <Typography
                        variant='body2'
                        sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                      >
                        {item.text}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Who We Serve */}
      <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: '#0d1b2a', color: '#fff' }}>
        <Container maxWidth='lg'>
          <Typography
            variant='h3'
            align='center'
            sx={{
              fontWeight: 700,
              mb: 1,
              fontSize: { xs: '1.8rem', md: '2.5rem' },
            }}
          >
            Who We Serve
          </Typography>
          <Typography
            variant='subtitle1'
            align='center'
            sx={{ color: '#90e0ef', mb: 6 }}
          >
            Trusted by leading organizations across the commercial real estate
            industry.
          </Typography>
          <Grid
            container
            spacing={2}
            justifyContent='center'
          >
            {whoWeServe.map((client) => (
              <Grid
                size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
                key={client}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    py: 1.5,
                    px: 2,
                    borderRadius: 2,
                    bgcolor: 'rgba(255,255,255,0.05)',
                  }}
                >
                  <Handshake sx={{ color: '#00b4d8', fontSize: 20 }} />
                  <Typography
                    variant='body2'
                    sx={{ fontWeight: 500 }}
                  >
                    {client}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA */}
      <Box sx={{ py: { xs: 6, md: 10 }, textAlign: 'center' }}>
        <Container maxWidth='md'>
          <Typography
            variant='h3'
            sx={{
              fontWeight: 700,
              mb: 3,
              color: '#0d1b2a',
              fontSize: { xs: '1.8rem', md: '2.5rem' },
            }}
          >
            Empower Your Next Real Estate Decision
          </Typography>
          <Typography
            variant='body1'
            sx={{ color: 'text.secondary', mb: 4, lineHeight: 1.7 }}
          >
            In today’s rapidly evolving commercial real estate market, access to
            reliable research is essential. SR Real Estate Services provides
            the intelligence and analysis needed to reduce uncertainty, uncover
            opportunities, and maximize long-term value.
          </Typography>
          <Typography
            variant='h6'
            sx={{ color: '#0d1b2a', mb: 2, fontWeight: 600 }}
          >
            Ready to learn more?
          </Typography>
          <Typography
            variant='body1'
            sx={{ color: 'text.secondary', mb: 4 }}
          >
            Contact us today to discuss your research needs and discover how our
            market intelligence solutions can support your next project or
            investment strategy.
          </Typography>
        </Container>
      </Box>

      {/* Contact Form */}
      <Box
        id='contact'
        sx={{ py: { xs: 6, md: 10 }, bgcolor: '#f8f9fa' }}
      >
        <Container maxWidth='sm'>
          <Typography
            variant='h3'
            align='center'
            sx={{
              fontWeight: 700,
              mb: 1,
              color: '#0d1b2a',
              fontSize: { xs: '1.8rem', md: '2.5rem' },
            }}
          >
            Contact Us
          </Typography>
          <Typography
            variant='subtitle1'
            align='center'
            sx={{ color: 'text.secondary', mb: 4 }}
          >
            Fill out the form below and we will get back to you shortly.
          </Typography>

          <Card
            sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}
          >
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Box
                component='form'
                onSubmit={formik.handleSubmit}
                noValidate
              >
                <Grid
                  container
                  spacing={2}
                >
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      required
                      id='firstName'
                      name='firstName'
                      label='First Name'
                      value={formik.values.firstName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.firstName &&
                        Boolean(formik.errors.firstName)
                      }
                      helperText={
                        formik.touched.firstName && formik.errors.firstName
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      required
                      id='lastName'
                      name='lastName'
                      label='Last Name'
                      value={formik.values.lastName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.lastName &&
                        Boolean(formik.errors.lastName)
                      }
                      helperText={
                        formik.touched.lastName && formik.errors.lastName
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      required
                      id='email'
                      name='email'
                      label='Email'
                      type='email'
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.email && Boolean(formik.errors.email)
                      }
                      helperText={formik.touched.email && formik.errors.email}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      id='phoneNumber'
                      name='phoneNumber'
                      label='Phone Number'
                      placeholder='+1 (___) ___-____'
                      value={formik.values.phoneNumber}
                      onChange={(e) =>
                        formik.setFieldValue(
                          'phoneNumber',
                          formatPhoneNumber(e.target.value)
                        )
                      }
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.phoneNumber &&
                        Boolean(formik.errors.phoneNumber)
                      }
                      helperText={
                        formik.touched.phoneNumber && formik.errors.phoneNumber
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      id='message'
                      name='message'
                      label='Message'
                      multiline
                      rows={4}
                      value={formik.values.message}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          id='contactConsent'
                          name='contactConsent'
                          checked={formik.values.contactConsent}
                          onChange={formik.handleChange}
                        />
                      }
                      label={
                        <Typography
                          variant='body2'
                          sx={{ color: 'text.secondary' }}
                        >
                          Click this box if you wish to be contacted by one of
                          our representatives
                        </Typography>
                      }
                    />
                  </Grid>
                </Grid>

                <Box sx={{ mt: 3 }}>
                  <LoaderButton
                    type='submit'
                    fullWidth
                    variant='contained'
                    size='large'
                    loading={sendMessageMutation.isLoading}
                    loadingText='Sending...'
                    disabled={!formik.dirty || !formik.isValid}
                  >
                    Send
                  </LoaderButton>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Container>
      </Box>

      {/* Footer */}
      <Box
        component='footer'
        sx={{ py: 3, bgcolor: '#0d1b2a', color: '#fff', textAlign: 'center' }}
      >
        <Container maxWidth='lg'>
          <Typography
            variant='body2'
            sx={{ color: '#90e0ef' }}
          >
            © {new Date().getFullYear()} SR Real Estate Services. All rights
            reserved.
          </Typography>
          <Typography
            variant='caption'
            sx={{ color: '#778da9', display: 'block', mt: 0.5 }}
          >
            SR REAL ESTATE SERVICES LLC · Formerly Solutions & Researches Pro
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};
